import React, { CSSProperties } from 'react';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { greys } from '../../../../styling/theme';
import NavReconGapValueCell from './NavReconGapValueCell.component';
import {
  addCommasToNumbersAndRound,
  percentageToNdecialPlacesNoHundred,
  percentageToNdecialPlacesNoHundredAddZeros,
  roundNumberToNDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import { NavReconPosition, NavReconPositionThresholds } from '../../../../api/rs-hg/nav-recon/types';
import {
  toNavReconGapThresholds,
  toNavReconPositionSizeChangeGapThresholds,
} from './navReconGapStatus';

export type NavReconPositionTableRow = {
  asset_class: string;
  isAggregateRow?: boolean;
  positions: NavReconPosition[];
  position_count: number;
  caceis_p_and_l_perc?: number;
  six_p_and_l_perc?: number;
  six_caceis_gap_perc?: number;
};

const cellBase: CSSProperties = { fontSize: '0.8rem', color: greys.grey900 };
const numericCell: CSSProperties = {
  ...cellBase,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  minWidth: '5rem',
};
const leftHeader: CSSProperties = {
  whiteSpace: 'nowrap',
  textAlign: 'left',
};
const centeredHeader: CSSProperties = {
  whiteSpace: 'nowrap',
  textAlign: 'center',
  minWidth: '5rem',
};

const detailColWidth = (width: string) => ({
  width,
  minWidth: width,
});

const detailNameCell = (width: string): CSSProperties => ({
  ...cellBase,
  textAlign: 'left',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  boxSizing: 'border-box',
  width,
  minWidth: width,
  maxWidth: width,
});

const detailNumericCell = (width: string): CSSProperties => ({
  ...cellBase,
  textAlign: 'center',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  boxSizing: 'border-box',
  ...detailColWidth(width),
});

const detailHeader = (
  width: string,
  align: 'left' | 'center' = 'center',
): CSSProperties => ({
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.25,
  textAlign: align,
  boxSizing: 'border-box',
  ...detailColWidth(width),
});

const detailColumnBorderLeft: CSSProperties = {
  borderLeft: `1px solid ${greys.grey300}`,
};

const detailColumnBorderRight: CSSProperties = {
  borderRight: `1px solid ${greys.grey300}`,
};

const detailColumnBorderLeftAndRight: CSSProperties = {
  ...detailColumnBorderLeft,
  ...detailColumnBorderRight,
};

const getDetailColumnDividerStyle = (
  field: keyof NavReconPosition,
): CSSProperties => {
  switch (field) {
    case 'caceis_p_and_l_perc':
      return detailColumnBorderLeft;
    case 'caceis_price_t_minus_1':
      return detailColumnBorderLeft;
    case 'used_six_price':
      return detailColumnBorderRight;
    case 'caceis_market_value_t':
      return detailColumnBorderLeftAndRight;
    case 'caceis_p_and_l':
      return detailColumnBorderRight;
    case 'fx_cross':
      return detailColumnBorderLeft;
    default:
      return {};
  }
};

type NavReconPositionDetailColumnConfig = {
  title: string;
  field: keyof NavReconPosition;
  width: string;
  render?: (row: NavReconPosition) => React.ReactNode;
};

type NavReconPositionGapField =
  | 'six_caceis_gap_perc'
  | 'six_caceis_price_diff_perc'
  | 'position_size_change_perc';

const renderGapValueCell =
  (field: NavReconPositionGapField, thresholds: NavReconPositionThresholds) =>
  (row: NavReconPosition) => {
    const gapThresholds =
      field === 'six_caceis_price_diff_perc'
        ? toNavReconGapThresholds(thresholds.price_variation_threshold)
        : field === 'position_size_change_perc'
          ? toNavReconPositionSizeChangeGapThresholds(
              thresholds.position_size_change_threshold,
            )
          : toNavReconGapThresholds(thresholds.p_and_l_threshold);

    return (
      <NavReconGapValueCell value={row[field]} thresholds={gapThresholds} />
    );
  };

const NAV_RECON_POSITION_DETAIL_COLUMN_CONFIG: NavReconPositionDetailColumnConfig[] =
  [
    { title: 'Name', field: 'name', width: '16rem' },
    {
      title: 'Internal asset code',
      field: 'internal_asset_code',
      width: '8rem',
      render: (row) => formatNullableString(row.internal_asset_code),
    },
    {
      title: 'ISIN',
      field: 'isin',
      width: '9rem',
      render: (row) => formatNullableString(row.isin),
    },
    { title: 'Asset type', field: 'asset_type', width: '8rem' },
    { title: 'Base currency', field: 'base_currency', width: '6rem' },
    {
      title: 'Caceis P/L %',
      field: 'caceis_p_and_l_perc',
      width: '7rem',
      render: (row) =>
        formatNullablePercent(
          row.caceis_p_and_l_perc,
          PL_PERCENT_DECIMALS,
          true,
        ),
    },
    {
      title: 'SIX P/L %',
      field: 'six_p_and_l_perc',
      width: '7rem',
      render: (row) =>
        formatNullablePercent(row.six_p_and_l_perc, PL_PERCENT_DECIMALS, true),
    },
    {
      title: 'SIX Caceis gap %',
      field: 'six_caceis_gap_perc',
      width: '8rem',
    },
    {
      title: 'Caceis price (T-1)',
      field: 'caceis_price_t_minus_1',
      width: '8rem',
      render: (row) =>
        formatNullableNumber(row.caceis_price_t_minus_1, PRICE_DECIMALS),
    },
    {
      title: 'Caceis price (T)',
      field: 'caceis_price_t',
      width: '8rem',
      render: (row) => formatNullableNumber(row.caceis_price_t, PRICE_DECIMALS),
    },
    {
      title: 'SIX price (T)',
      field: 'six_price_t',
      width: '8rem',
      render: (row) => formatNullableNumber(row.six_price_t, PRICE_DECIMALS),
    },
    {
      title: 'SIX Caceis price diff %',
      field: 'six_caceis_price_diff_perc',
      width: '8rem',
    },
    {
      title: 'Used SIX price',
      field: 'used_six_price',
      width: '6rem',
    },
    {
      title: 'Position size',
      field: 'position_size',
      width: '8rem',
      render: (row) => addCommasToNumbersAndRound(row.position_size),
    },
    {
      title: 'Position size change %',
      field: 'position_size_change_perc',
      width: '8rem',
    },
    {
      title: 'Caceis market value (T)',
      field: 'caceis_market_value_t',
      width: '9rem',
      render: (row) => addCommasToNumbersAndRound(row.caceis_market_value_t),
    },
    {
      title: 'Caceis P/L',
      field: 'caceis_p_and_l',
      width: '9rem',
      render: (row) => addCommasToNumbersAndRound(row.caceis_p_and_l),
    },
    {
      title: 'Base currency market return %',
      field: 'base_currency_market_return_perc',
      width: '7rem',
      render: (row) =>
        formatNullablePercent(row.base_currency_market_return_perc),
    },
    { title: 'FX cross', field: 'fx_cross', width: '7rem' },
    {
      title: 'FX rate (T-1)',
      field: 'fx_rate_t_minus_1',
      width: '8rem',
      render: (row) =>
        formatNullableNumber(row.fx_rate_t_minus_1, PRICE_DECIMALS),
    },
    {
      title: 'FX rate (T)',
      field: 'fx_rate_t',
      width: '8rem',
      render: (row) => formatNullableNumber(row.fx_rate_t, PRICE_DECIMALS),
    },
  ];

export const NAV_RECON_POSITION_DETAIL_COLUMN_WIDTHS =
  NAV_RECON_POSITION_DETAIL_COLUMN_CONFIG.map((column) => column.width);

export const NAV_RECON_POSITION_DETAIL_TABLE_WIDTH = `${NAV_RECON_POSITION_DETAIL_COLUMN_WIDTHS.reduce(
  (total, width) => total + parseFloat(width),
  0,
)}rem`;

const PERCENT_DECIMALS = 2;
const PL_PERCENT_DECIMALS = 4;
const PRICE_DECIMALS = 4;

const formatNullableNumber = (
  value: number | null | undefined,
  decimals?: number,
) => {
  if (value == null || Number.isNaN(value)) return '-';
  return decimals != null
    ? roundNumberToNDecimalPlaces(value, decimals)
    : addCommasToNumbersAndRound(value);
};

const formatNullablePercent = (
  value: number | null | undefined,
  decimals: number = PERCENT_DECIMALS,
  fixedDecimals = false,
) => {
  if (value == null || Number.isNaN(value)) return '-';
  return fixedDecimals
    ? percentageToNdecialPlacesNoHundredAddZeros(value, decimals)
    : percentageToNdecialPlacesNoHundred(value, decimals);
};

const formatNullableString = (value: string | null | undefined) =>
  value ?? '-';

export const getNavReconPositionsColumns = (
  thresholds: NavReconPositionThresholds,
): CustomColumn<NavReconPositionTableRow>[] => [
    {
      title: 'Asset Class',
      field: 'asset_class',
      cellStyle: (_value, row) => ({
        ...cellBase,
        fontWeight: row.isAggregateRow ? 600 : 400,
        whiteSpace: 'nowrap',
      }),
      headerStyle: leftHeader,
    },
    {
      title: 'Positions count',
      field: 'position_count',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
    },
    {
      title: 'Caceis P/L',
      field: 'caceis_p_and_l_perc',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) =>
        formatNullablePercent(
          row.caceis_p_and_l_perc,
          PL_PERCENT_DECIMALS,
          true,
        ),
    },
    {
      title: 'SIX P/L',
      field: 'six_p_and_l_perc',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) =>
        formatNullablePercent(row.six_p_and_l_perc, PL_PERCENT_DECIMALS, true),
    },
    {
      title: 'P/L Gap',
      field: 'six_caceis_gap_perc',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => (
        <NavReconGapValueCell
          value={row.six_caceis_gap_perc}
          thresholds={toNavReconGapThresholds(thresholds.p_and_l_threshold)}
        />
      ),
    },
  ];

export const getNavReconPositionDetailColumns = (
  thresholds: NavReconPositionThresholds,
): CustomColumn<NavReconPosition>[] =>
  NAV_RECON_POSITION_DETAIL_COLUMN_CONFIG.map(
    ({ title, field, width, render }) => {
      const gapRender =
        field === 'six_caceis_gap_perc' ||
        field === 'six_caceis_price_diff_perc' ||
        field === 'position_size_change_perc'
          ? renderGapValueCell(field, thresholds)
          : render;
        const baseCellStyle =
          field === 'name'
            ? detailNameCell(width)
            : detailNumericCell(width);
        const dividerStyle = getDetailColumnDividerStyle(field);

        const cellRender = gapRender ?? render;

        return {
          title,
          field,
          width,
          cellStyle: { ...baseCellStyle, ...dividerStyle },
          headerStyle: {
            ...detailHeader(width, field === 'name' ? 'left' : 'center'),
            ...dividerStyle,
          },
          ...(cellRender ? { render: cellRender } : {}),
        };
      },
    );
